/**
 * DayView (Plan 03-07; restylet i #157, steg 7a).
 *
 * Dag beholder tidslinjen, i samme stil som agendaen i Uke (D8): overskriften
 * «Fredag 8. mai», heldagshendelsene som agendarader, og timeblokkene som
 * glasskort med fargestripe og deltakerne som InitialAvatar-stabel. Uka står
 * i datopanelet, så WeekChip er borte herfra.
 *
 * Single-day vertical timeline with:
 *   - header: "Fredag 8. mai" (formatDayHeader)
 *   - All-day events block (agenda EventCard)
 *   - Timed events: absolute positioning in 48px/hour hour rail
 *   - "Now" indicator: primary hairline + 6px filled circle, ticks every 60s
 *     (reduced-motion: static line, no pulse — CAL_TIMINGS.nowLineTick)
 *   - Push highlight: highlightEventId prop adds outline pulse for Plan 03-09
 *   - Empty state: calendar.dayView.empty + Legg til hendelse CTA
 *
 * D-06: hand-rolled; no external calendar library.
 * D-14: formatDayHeader from formatters.ts.
 * T-CAL-34: highlightEventId used only for class toggle — never innerHTML.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isSameDay } from 'date-fns';
import { EmptyState } from '../../components/primitives';
import { EventCard } from './EventCard';
import { CAL_TIMINGS } from './timings';
import { capitalizeFirst, formatDayHeader } from '../../i18n/formatters';
import { attendeesOf, NO_ATTENDEES, type AttendeeLookup } from './attendees';
import type { EventInstance } from './useCalendarRange';

const HOUR_HEIGHT_PX = 48;
const VISIBLE_START_HOUR = 6;
const VISIBLE_END_HOUR = 22;

/** Push-uthevingen (Plan 03-09): omriss som pulserer, statisk med redusert bevegelse. */
const HIGHLIGHT =
  'rounded-row outline outline-2 outline-cal-today-ring outline-offset-2 motion-safe:animate-pulse';

/** Timeetikett i venstremargen, «06:00». */
const hourLabel = (hour: number) => `${String(hour).padStart(2, '0')}:00`;

export interface DayViewProps {
  anchor: Date;
  events: EventInstance[];
  /** Navn og farge per deltaker; uten kjente deltakere står AttendeeChips. */
  attendees?: AttendeeLookup;
  /** If set, this event id gets a push-deep-link outline pulse (Plan 03-09). */
  highlightEventId?: string;
  onEventClick?: (e: EventInstance) => void;
  onEmptySlotClick?: (date: Date) => void;
}

export function DayView({
  anchor,
  events,
  attendees = NO_ATTENDEES,
  highlightEventId,
  onEventClick,
  onEmptySlotClick,
}: DayViewProps) {
  const { t } = useTranslation();
  const [now, setNow] = useState<Date>(() => new Date());
  const isToday = isSameDay(anchor, now);

  // Live "now" indicator — tick every minute. Only when viewing today.
  useEffect(() => {
    if (!isToday) return;
    const id = setInterval(() => {
      setNow(new Date());
    }, CAL_TIMINGS.nowLineTick);
    return () => clearInterval(id);
  }, [isToday]);

  const allDayEvents = events.filter((e) => e.all_day);
  const timedEvents = events.filter(
    (e) => !e.all_day && isSameDay(new Date(e.starts_at), anchor),
  );

  const nowMins = isToday
    ? (now.getHours() - VISIBLE_START_HOUR) * 60 + now.getMinutes()
    : 0;
  const nowTop = (nowMins / 60) * HOUR_HEIGHT_PX;

  const gridHeight = (VISIBLE_END_HOUR - VISIBLE_START_HOUR) * HOUR_HEIGHT_PX;

  /**
   * Påminnelsen lenker med seriens id (event-reminders.service), mens en
   * gjentakelses instans har id `series_id|occ_iso` (D-04). Datoen i lenken
   * velger dagen, så seriens id peker ut forekomsten (#157). Gjelder også
   * heldagshendelser.
   */
  const isLinked = (ev: EventInstance): boolean =>
    highlightEventId !== undefined &&
    (ev.id === highlightEventId || ev.series_id === highlightEventId);

  /** Pixel offset from VISIBLE_START_HOUR for a timed starts_at. */
  function topOffset(startsAt: string): number {
    const d = new Date(startsAt);
    const mins = (d.getHours() - VISIBLE_START_HOUR) * 60 + d.getMinutes();
    return (mins / 60) * HOUR_HEIGHT_PX;
  }

  /** Pixel height — minimum 30 minutes = 24px. */
  function eventHeight(startsAt: string, endsAt: string | null): number {
    const start = new Date(startsAt);
    const end = endsAt
      ? new Date(endsAt)
      : new Date(start.getTime() + 3_600_000);
    const durMins = Math.max(30, (end.getTime() - start.getTime()) / 60_000);
    return (durMins / 60) * HOUR_HEIGHT_PX;
  }

  return (
    <section
      aria-label={t('calendar.dayView.aria', { date: formatDayHeader(anchor) })}
      data-testid="day-view"
      className="flex flex-col gap-4 md:max-w-3xl"
    >
      {/* ── Header ─────────────────────────────────────────────────── */}
      <h2 className="m-0 text-heading-s font-extrabold" data-testid="day-header">
        {capitalizeFirst(formatDayHeader(anchor))}
      </h2>

      {/* ── All-day events block ───────────────────────────────────── */}
      {allDayEvents.length > 0 && (
        <div
          className="flex flex-col gap-2.5"
          data-testid="day-allday-block"
        >
          {allDayEvents.map((ev) => (
            <div
              key={ev.id}
              data-testid={isLinked(ev) ? 'day-event-highlighted' : `day-event-${ev.id}`}
              className={isLinked(ev) ? HIGHLIGHT : undefined}
            >
              <EventCard
                variant="agenda"
                attendees={attendeesOf(ev, attendees)}
                id={ev.id}
                title={ev.title}
                starts_at={ev.starts_at}
                ends_at={ev.ends_at}
                all_day={ev.all_day}
                location={ev.location}
                color_token={ev.color_token}
                attendee_ids={ev.attendee_ids}
                onClick={() => onEventClick?.(ev)}
              />
            </div>
          ))}
        </div>
      )}

      {/* ── Empty state ───────────────────────────────────────────────── */}
      {/* Plan 06-02 Task 2: <EmptyState variant="inline"> with an inline
          "Legg til hendelse" CTA wired to onEmptySlotClick. Outer wrapper
          preserves the day-empty data-testid contract. #157: står over
          tidslinjen, så den synes uten å rulle forbi en tom dag, og en annen
          dag enn i dag sier ikke «i dag». */}
      {events.length === 0 && (
        <div data-testid="day-empty">
          <EmptyState
            variant="inline"
            heading={isToday ? t('calendar.empty.day.heading') : t('calendar.agenda.emptyDay')}
            {...(isToday ? { body: t('calendar.empty.day.body') } : {})}
            cta={{ label: t('calendar.action.addEvent'), onClick: () => onEmptySlotClick?.(anchor) }}
          />
        </div>
      )}

      {/* ── Timed timeline ───────────────────────────────────────────── */}
      <div
        className="grid grid-cols-[3rem_1fr] gap-2 relative"
        style={{ height: `${gridHeight}px` }}
        data-testid="day-timeline"
        onClick={() => onEmptySlotClick?.(anchor)}
      >
        {/* Hour rail */}
        <div className="relative" aria-hidden="true">
          {Array.from(
            { length: VISIBLE_END_HOUR - VISIBLE_START_HOUR + 1 },
            (_, i) => (
              <div
                key={i}
                className="absolute right-1 text-label text-ink-2 tnum font-semibold leading-none"
                style={{ top: `${i * HOUR_HEIGHT_PX - 6}px` }}
              >
                {hourLabel(VISIBLE_START_HOUR + i)}
              </div>
            ),
          )}
        </div>

        {/* Day column */}
        <div className="relative border-l border-card-line">
          {/* Hourly hairlines */}
          {Array.from(
            { length: VISIBLE_END_HOUR - VISIBLE_START_HOUR + 1 },
            (_, i) => (
              <div
                key={i}
                className="absolute left-0 right-0 border-t border-card-line"
                style={{ top: `${i * HOUR_HEIGHT_PX}px` }}
              />
            ),
          )}

          {/* Timed events */}
          {timedEvents.map((ev) => {
            const top = topOffset(ev.starts_at);
            const height = eventHeight(ev.starts_at, ev.ends_at);
            const isHighlighted = isLinked(ev);
            return (
              <div
                key={ev.id}
                data-testid={
                  isHighlighted
                    ? 'day-event-highlighted'
                    : `day-event-${ev.id}`
                }
                className={[
                  'absolute left-2 right-2',
                  isHighlighted ? HIGHLIGHT : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{ top: `${top}px`, height: `${height}px` }}
                onClick={(e) => {
                  e.stopPropagation();
                  onEventClick?.(ev);
                }}
              >
                <EventCard
                  variant="medium"
                  attendees={attendeesOf(ev, attendees)}
                  id={ev.id}
                  title={ev.title}
                  starts_at={ev.starts_at}
                  ends_at={ev.ends_at}
                  all_day={ev.all_day}
                  location={ev.location}
                  color_token={ev.color_token}
                  attendee_ids={ev.attendee_ids}
                />
              </div>
            );
          })}

          {/* "Now" indicator — primary hairline + circle dot */}
          {isToday && (
            <div
              data-testid="now-line"
              className="absolute left-0 right-0 z-10 pointer-events-none"
              style={{ top: `${nowTop}px` }}
            >
              {/* left-edge circle dot */}
              <div className="absolute -left-0.75 -top-0.75 w-1.5 h-1.5 rounded-full bg-primary" />
              {/* hairline */}
              <div className="border-t border-primary w-full" />
            </div>
          )}
        </div>
      </div>

    </section>
  );
}
